/**
 * usePortraitVideo mixin
 *
 * 竖屏视频 H5 滑动缩放逻辑。
 * 职责：
 *  - 检测视频是否竖屏 (videoHeight > videoWidth)
 *  - 管理视频区域高度的 touch 缩放 (maxH ↔ minH)
 *  - 通过 CSS 变量 --video-h 驱动布局
 *  - provide 状态给子组件
 *
 * 用法：
 *  在页面组件中 mixins: [usePortraitVideo]
 *  监听子组件 @videoOrientation 事件调用 onVideoOrientationDetected
 */

import { isH5 } from '@saas/components/src/utils/utils'

// 吸附阈值 (px)
const SNAP_THRESHOLD = 30

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
}

export default {
    data() {
        return {
            // ---- 竖屏视频状态 ----
            isPortraitVideo: false,
            portraitVideoHeight: 0, // 当前高度 (px)
            isPortraitCollapsed: false, // 是否缩到最小
            _pvIsSnapping: false, // 内部：是否正在吸附动画
            _pvTouchStartY: 0,
            _pvTouchStartH: 0,
            _pvTouchActive: false,
        }
    },

    computed: {
        /** 竖屏视频最大高度：屏幕高度 × 0.7 */
        pvMaxHeight() {
            return Math.round(window.innerHeight * 0.7)
        },
        /** 竖屏视频最小高度：16:9 比例 (screenW × 9/16) */
        pvMinHeight() {
            return Math.round(window.innerWidth * 9 / 16)
        },
        /** 是否为 H5 竖屏模式（同时满足 H5 + 竖屏视频） */
        isH5PortraitMode() {
            return isH5() && this.isPortraitVideo
        },
    },

    watch: {
        isPortraitVideo(val) {
            if (val && isH5()) {
                this.portraitVideoHeight = this.pvMaxHeight
                this._applyVideoHeight(this.pvMaxHeight)
                this.$nextTick(() => {
                    this._pvBindTouch()
                })
            } else {
                this._pvUnbindTouch()
                this._removeVideoHeight()
            }
        },
    },

    beforeDestroy() {
        this._pvUnbindTouch()
    },

    methods: {
        /**
         * 由子组件 VideoCard 在视频 loadeddata 后调用
         * @param {{ width: number, height: number }} dimensions
         */
        onVideoOrientationDetected({ width, height }) {
            if (!isH5()) return
            this.isPortraitVideo = height > width
        },

        // ---- CSS 变量操作 ----

        _applyVideoHeight(h) {
            const el = this.$el
            if (el) {
                el.style.setProperty('--video-h', h + 'px')
            }
        },

        _removeVideoHeight() {
            const el = this.$el
            if (el) {
                el.style.removeProperty('--video-h')
            }
        },

        // ---- Touch 事件 ----

        _pvBindTouch() {
            const contentEl = this._pvGetTouchArea()
            if (!contentEl) return
            contentEl.addEventListener('touchstart', this._pvOnTouchStart, { passive: false })
            contentEl.addEventListener('touchmove', this._pvOnTouchMove, { passive: false })
            contentEl.addEventListener('touchend', this._pvOnTouchEnd, { passive: true })
        },

        _pvUnbindTouch() {
            const contentEl = this._pvGetTouchArea()
            if (!contentEl) return
            contentEl.removeEventListener('touchstart', this._pvOnTouchStart)
            contentEl.removeEventListener('touchmove', this._pvOnTouchMove)
            contentEl.removeEventListener('touchend', this._pvOnTouchEnd)
        },

        /** 获取非视频区域（tabs + tab-content） */
        _pvGetTouchArea() {
            return this.$el?.querySelector('.content-wrap')
        },

        _pvOnTouchStart(e) {
            // 如果触摸在视频区域内，不处理
            if (this._pvIsTouchInVideoArea(e)) return
            this._pvTouchActive = true
            this._pvTouchStartY = e.touches[0].clientY
            this._pvTouchStartH = this.portraitVideoHeight

            // 关闭吸附动画
            this._pvSetSnapping(false)
        },

        _pvOnTouchMove(e) {
            if (!this._pvTouchActive) return

            const deltaY = this._pvTouchStartY - e.touches[0].clientY // 上滑为正
            const newH = clamp(this._pvTouchStartH - deltaY, this.pvMinHeight, this.pvMaxHeight)

            // 如果高度在变化中，阻止默认滚动
            if (newH !== this.pvMinHeight && newH !== this.pvMaxHeight) {
                e.preventDefault()
            }

            // 到达最小/最大高度时，如果 tab-content 可以滚动则允许
            if (newH === this.pvMinHeight && deltaY > 0) {
                // 已到最小，允许内容区域滚动
                this._pvTouchActive = false
                return
            }

            this.portraitVideoHeight = newH
            this._applyVideoHeight(newH)
        },

        _pvOnTouchEnd() {
            if (!this._pvTouchActive) return
            this._pvTouchActive = false

            // 吸附
            this._pvSetSnapping(true)
            let targetH = this.portraitVideoHeight
            if (this.portraitVideoHeight < this.pvMinHeight + SNAP_THRESHOLD) {
                targetH = this.pvMinHeight
            } else if (this.portraitVideoHeight > this.pvMaxHeight - SNAP_THRESHOLD) {
                targetH = this.pvMaxHeight
            }

            this.portraitVideoHeight = targetH
            this.isPortraitCollapsed = (targetH === this.pvMinHeight)
            this._applyVideoHeight(targetH)

            // 动画结束后关闭 transition
            setTimeout(() => {
                this._pvSetSnapping(false)
            }, 300)
        },

        _pvIsTouchInVideoArea(e) {
            const videoArea = this.$el?.querySelector('.video-area')
            if (!videoArea) return false
            const rect = videoArea.getBoundingClientRect()
            const touch = e.touches[0]
            return (
                touch.clientX >= rect.left &&
                touch.clientX <= rect.right &&
                touch.clientY >= rect.top &&
                touch.clientY <= rect.bottom
            )
        },

        _pvSetSnapping(val) {
            this._pvIsSnapping = val
            const videoArea = this.$el?.querySelector('.video-area')
            if (videoArea) {
                if (val) {
                    videoArea.classList.add('pv-snapping')
                } else {
                    videoArea.classList.remove('pv-snapping')
                }
            }
        },
    },
}
